/**
 * Voice & Consent — co-located API client (P5 Wave 4 §6, read + one revoke).
 *
 * Talks to quill-admin-backend `/api/admin/quill/voice-consent/*`:
 *   GET  /member/:id                  → voice-DNA status + consent-ledger state
 *   GET  /member/:id/mirror-access    → active mirror-access grants (metadata)
 *   POST /mirror-access/:id/revoke    → revoke one grant (the ONLY mutation)
 *
 * The shared `request`/`unwrap` helpers in services/quillAdminService.ts are
 * module-private, so — per the contract — the tiny fetch/JSON + envelope helper
 * is replicated here; the exported `QuillApiError` is reused so status/`code`
 * handling matches every other Quill surface. A missing member surfaces as a
 * 404 (`QuillApiError.status === 404`) which the page turns into a friendly
 * EmptyState.
 *
 * SECURITY (contract §6): mirror-access grants are hash-stored server-side and
 * the backend returns METADATA ONLY — never a raw token. Nothing in these types
 * carries a token field, and the page never renders one.
 */

import { QuillApiError } from '../../../services/quillAdminService';

export { QuillApiError };

const BASE = '/api/admin/quill';

interface EnvelopeShape {
  success?: boolean;
  code?: string;
  message?: string;
  error?: string;
  data?: unknown;
  [key: string]: unknown;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init.headers || {}),
      },
    });
  } catch (e) {
    throw new QuillApiError(
      e instanceof Error ? e.message : 'Network error contacting Quill backend',
      0,
      'NETWORK_ERROR'
    );
  }

  let body: EnvelopeShape | null = null;
  try {
    body = await res.json();
  } catch {
    // No/invalid JSON body — handled below.
  }

  if (!res.ok || (body && body.success === false)) {
    const message = body?.message || body?.error || `Quill backend request failed (${res.status})`;
    throw new QuillApiError(message, res.status, body?.code);
  }

  return (body ?? {}) as T;
}

/** Unwrap `{ success, data }` → data; falls back to the raw body (flat routes). */
function unwrap<T>(body: unknown): T {
  const b = body as { data?: T } | null | undefined;
  if (b && typeof b === 'object' && 'data' in b && b.data !== undefined) {
    return b.data as T;
  }
  return body as T;
}

// ── Types (contract §6 fields — no token field anywhere, by design) ─────────

/** Voice-DNA / clone status for a member — shaped against the `voice_dna` object
 *  voiceConsent.js actually returns. Any field may be null pre-build. Numeric
 *  aggregates may arrive as node-pg strings, so scores are `number | string`. */
export interface VoiceDnaStatus {
  /** True when an active clone build is ready for playback. */
  ready: boolean;
  /** Voice Fidelity Score, cosine(clone, master) ∈ [0,1]. */
  fidelity_score: number | string | null;
  /** Best speaker-verification cosine across verified samples. */
  cosine: number | string | null;
  verified_sample_count?: number | string | null;
  voice_model_version?: number | null;
  build_type?: string | null;
  pitch_delta?: number | string | null;
  pace_delta?: number | string | null;
  dataset_seconds?: number | string | null;
  /** Timestamp of the most recent clone build (`built_at`). */
  last_rebuild?: string | null;
  /** No column persists a watermark yet — the backend returns null (honest
   *  placeholder). Rendered as "not tracked" rather than invented. */
  watermark?: string | null;
  use_cloned_voice?: boolean | null;
  preference_updated_at?: string | null;
}

/** AB2602 consent-ledger state for a member — shaped against the `consent`
 *  object voiceConsent.js returns (`{version, present, active, ...}`). There is
 *  no server-derived `ab2602_state`; the page derives a label from these. */
export interface ConsentState {
  version?: string | null;
  /** True when a consent row exists for this member + version. */
  present?: boolean;
  /** True when the consent is acknowledged, un-revoked, and un-expired. */
  active?: boolean;
  acknowledged_at?: string | null;
  expires_at?: string | null;
  revoked_at?: string | null;
  revoked_reason?: string | null;
}

/** `GET /member/:id` — flat body. */
export interface VoiceConsentProfile {
  success?: boolean;
  member_id: string;
  voice_dna: VoiceDnaStatus | null;
  consent: ConsentState | null;
}

/** One active mirror-access grant — METADATA ONLY (never a raw token). */
export interface MirrorAccessGrant {
  id: string;
  /** The member's nickname for the invitee, e.g. "Sarah". */
  asker_label: string | null;
  expires_at: string | null;
  last_used_at?: string | null;
  created_at?: string | null;
}

export interface MirrorAccessResponse {
  success?: boolean;
  grants: MirrorAccessGrant[];
}

export const voiceConsentApi = {
  /** GET /member/:id — voice-DNA + consent state. 404 → member has no Quill voice profile. */
  getMember: (memberId: string) =>
    request<VoiceConsentProfile>(`/voice-consent/member/${encodeURIComponent(memberId)}`),

  /** GET /member/:id/mirror-access — active grants (metadata only). */
  getMirrorAccess: (memberId: string) =>
    request<MirrorAccessResponse>(
      `/voice-consent/member/${encodeURIComponent(memberId)}/mirror-access`
    ).then((body) => ({ grants: unwrap<MirrorAccessGrant[]>(body.grants) ?? [] })),

  /** POST /mirror-access/:id/revoke — the ONLY mutation. Change-logged server-side. */
  revokeMirrorAccess: (grantId: string, reason: string) =>
    request<{ success?: boolean; message?: string }>(
      `/voice-consent/mirror-access/${encodeURIComponent(grantId)}/revoke`,
      { method: 'POST', body: JSON.stringify({ reason }) }
    ),
};

export default voiceConsentApi;
